'use client';

import { Product, ProductOption, OptionValue } from '@/types';

interface PriceTableProps {
  product: Product;
}

interface PriceTableRow {
  labels: string[];
  price: number;
}

/**
 * PriceTable Component
 *
 * Displays a compact price table for products that have options marked with `forPriceTable`.
 * If multiple options are marked as forPriceTable, it creates a multiplication matrix
 * showing all combinations and their resulting prices.
 */
export default function PriceTable({ product }: PriceTableProps) {
  // Get options marked for price table
  const priceTableOptions = product.options?.filter(
    (opt) => opt.forPriceTable && opt.values && opt.values.length > 0
  ) || [];

  // If no price table options, return null (caller should show regular price)
  if (priceTableOptions.length === 0) {
    return null;
  }

  // Generate all combinations of option values
  const generateCombinations = (
    options: ProductOption[],
    currentIndex: number = 0,
    currentLabels: string[] = [],
    currentPriceModifier: number = 0
  ): PriceTableRow[] => {
    // Base case: we've processed all options
    if (currentIndex >= options.length) {
      return [{
        labels: currentLabels,
        price: product.price + currentPriceModifier
      }];
    }

    const currentOption = options[currentIndex];
    const availableValues = currentOption.values.filter(v => v.isAvailable !== false);

    // If no available values, skip this option
    if (availableValues.length === 0) {
      return generateCombinations(options, currentIndex + 1, currentLabels, currentPriceModifier);
    }

    const results: PriceTableRow[] = [];

    for (const value of availableValues) {
      const newLabels = [...currentLabels, value.nameHe || value.name];
      const newPriceModifier = currentPriceModifier + (value.priceModifier || 0);

      const combinations = generateCombinations(
        options,
        currentIndex + 1,
        newLabels,
        newPriceModifier
      );

      results.push(...combinations);
    }

    return results;
  };

  const combinations = generateCombinations(priceTableOptions);

  // If only one combination (or none), show simple format
  if (combinations.length <= 1) {
    if (combinations.length === 1) {
      return (
        <div className="text-2xl font-bold text-blue-600">
          {combinations[0].labels.join(' - ')} - ₪{combinations[0].price.toFixed(2)}
        </div>
      );
    }
    return null;
  }

  // Get option headers
  const headers = priceTableOptions.map(opt => opt.nameHe || opt.name);

  return (
    <div className="text-2xl">
      <table className="w-full border-collapse">
        {/* Column titles row - commented out per request
        <thead>
          <tr className="text-gray-600">
            {headers.map((header, idx) => (
              <th key={idx} className="text-right px-2 py-1 font-medium text-2xl">
                {header}
              </th>
            ))}
            <th className="text-right px-2 py-1 font-medium text-2xl">
              מחיר
            </th>
          </tr>
        </thead>
        */}
        <tbody>
          {combinations.map((row, rowIdx) => (
            <tr key={rowIdx} className={rowIdx % 2 === 0 ? 'bg-gray-50' : ''}>
              {row.labels.map((label, labelIdx) => (
                <td key={labelIdx} className="px-2 py-1 text-gray-700 text-2xl">
                  {label}
                </td>
              ))}
              <td className="px-2 py-1 font-bold text-blue-600 text-2xl">
                ₪{row.price.toFixed(2)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * Helper function to check if a product has price table options
 */
export function hasPriceTableOptions(product: Product): boolean {
  return product.options?.some(
    (opt) => opt.forPriceTable && opt.values && opt.values.length > 0
  ) || false;
}
